import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ModalComponent,
	AlertComponent,
	FormGroupComponent,
	ToggleComponent,
	TextareaComponent,
	IToogleSelect,
} from '@tecalisdev/infinity';
import { ReactiveFormsService } from '../../services/reactive-forms.service';
import { LoadingService } from '../../services/loading.service';
import { MasterService } from '../../services/master.service';
import { PROCESS_STATUS } from '../../../processes/models/processes.models';
import { ProcessService } from '../../../processes/services/process.service';
import { OperationsService } from '../../../operations/services/operations.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from '../../services/snackbar.service';
import { OPERATION_STATUS } from '../../../operations/models/operations.models';

@Component({
	selector: 'app-modal-change-status',
	imports: [
		ReactiveFormsModule,
		ModalComponent,
		TranslatePipe,
		AlertComponent,
		FormGroupComponent,
		ToggleComponent,
		TextareaComponent,
	],
	templateUrl: './modal-change-status.component.html',
	styleUrl: './modal-change-status.component.scss',
})
export class ModalChangeStatusComponent implements OnInit {
	public reactiveFormService = inject(ReactiveFormsService);
	private loading = inject(LoadingService);
	private masterService = inject(MasterService);
	private processService = inject(ProcessService);
	private operationsService = inject(OperationsService);
	private trans = inject(TranslateService);
	private snackbarService = inject(SnackbarService);

	@Input() status: PROCESS_STATUS | OPERATION_STATUS;
	@Input() id: string;
	@Input() statusEntity: 'process' | 'operation' = 'process';
	@Output() closeEvent = new EventEmitter<boolean>();

	public errorMsg: string = '';
	public form: FormGroup = new FormGroup({
		status: new FormControl('', [Validators.required]),
		reasons: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(500)]),
	});
	public options?: IToogleSelect[];

	constructor() {
		this.options = this.masterService.changeStatusToggleOptions;
	}

	ngOnInit(): void {
		if (!this.status) {
			return;
		}
		const currentUuid = this.masterService.getUUIDStatusByName(this.status);
		const currentOption = this.options?.find((option) => option.id === currentUuid);
		if (currentOption) {
			this.form.patchValue({ status: currentOption });
		}
	}

	public closeModal(type: string) {
		const actions = {
			buttonPrimary: () => {
				if (this.form.invalid) {
					this.form.markAllAsTouched();
					return;
				}
				const trimmedReasons = this.form.value?.reasons?.trim();
				if (!trimmedReasons) {
					this.form.get('reasons')?.setErrors({ required: true });
					this.form.get('reasons')?.markAsTouched();
					return;
				}
				const selectedId = this.form.value.status?.id ?? this.form.value.status;
				if (selectedId === this.masterService.getUUIDStatusByName(this.status)) {
					this.errorMsg = this.trans.instant('PROCESSES.DETAIL.CHANGE_STATUS_SAME_STATUS');
					return;
				}
				this.updateStatus();
			},
		};
		actions[type] ? actions[type]() : this.closeEvent.emit(false);
	}

	private updateStatus(): void {
		const uuid = this.form?.value?.status?.id;
		if (!uuid) return;

		const reasons = this.form.value?.reasons?.trim() ?? null;

		const loadingKey =
			this.statusEntity === 'operation' ? 'LOADING.UPDATE_STATUS_OPERATION' : 'LOADING.UPDATE_STATUS_PROCESS';
		this.loading.show(this.trans.instant(loadingKey));
		const request$ =
			this.statusEntity === 'operation'
				? this.operationsService.updateStatus(this.id, uuid, reasons)
				: this.processService.updateStatus(this.id, uuid, reasons);

		request$.subscribe({
			next: (response: any) => {
				this.loading.hide();
				this.closeEvent.emit(true);
				const confirmKey =
					this.statusEntity === 'operation'
						? 'OPERATIONS.DETAIL.UPDATE_STATUS_OPERATION_CONFIRM'
						: 'PROCESSES.DETAIL.UPDATE_STATUS_PROCESS_CONFIRM';
				this.snackbarService.show(this.trans.instant(confirmKey));
			},
			error: (error: HttpErrorResponse) => {
				if (this.hasMissingReasonError(error)) {
					this.errorMsg = this.trans.instant('PROCESSES.DETAIL.CHANGE_STATUS_REASON_REQUIRED');
					this.loading.hide();
					return;
				}

				const errorKey =
					this.statusEntity === 'operation'
						? 'OPERATIONS.DETAIL.CHANGE_STATUS_OPERATION_ERROR'
						: 'PROCESSES.DETAIL.CHANGE_STATUS_PROCESS_ERROR';
				this.errorMsg = this.trans.instant(errorKey);
				this.loading.hide();
			},
		});
	}

	private hasMissingReasonError(error: HttpErrorResponse): boolean {
		const reasonErrors = error.error?.errors?.reasons;
		if (!Array.isArray(reasonErrors)) {
			return false;
		}

		return reasonErrors.includes('STATUS_CHANGE_REASON_REQUIRED');
	}
}
